/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from "react";
import { formatTimeIST } from "../../utils/function";
import { type Schedule } from "../../api/schedule";
import { ScheduleAudioPlayer } from "./CustomAudioPlayer";

interface Props {
  selectedSchedule: Schedule | null;
  setSelectedSchedule: (schedule: Schedule | null) => void;
  checkingMp3: boolean;
  mp3Exists: boolean | null;
  currentPlaying?: boolean; 
  setPlaying?: any;
}

export function ScheduleDetailsPopup({
  selectedSchedule,
  setSelectedSchedule,
  checkingMp3,
  mp3Exists,
  currentPlaying,
  setPlaying,
}: Props) {
  const modalRef = useRef<HTMLDivElement>(null);

  /* ---------------- Disable Background Scroll ---------------- */
  useEffect(() => {
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  /* ---------------- Close On Outside Click ---------------- */
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      setSelectedSchedule(null);
    }
  };

  if (!selectedSchedule) return null;

  return (
    <div
      onClick={handleOutsideClick}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
    >
      <div
        ref={modalRef}
        className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6 w-full max-w-2xl relative shadow-xl"
      >
        {/* Close */}
        <button
          onClick={() => setSelectedSchedule(null)}
          className="absolute top-3 right-3 text-white/70 hover:text-white text-lg"
        >
          ✕
        </button>

        {/* Header */}
        <div className="flex gap-4 mb-6">
          <div className="w-16 h-16 rounded-xl bg-linear-to-b from-[#174868] to-[#55afba] flex items-center justify-center shrink-0 overflow-hidden">
            {selectedSchedule?.show?.thumbnail ? (
              <img
                src={selectedSchedule?.show?.thumbnail}
                alt={selectedSchedule?.show?.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white text-xl font-bold">
                {selectedSchedule?.show?.name?.charAt(0)}
              </span>
            )}
          </div>

          <div>
            <p className="text-[#55afba] text-sm font-medium">
              {formatTimeIST(selectedSchedule?.startTime)} -{" "}
              {formatTimeIST(selectedSchedule?.endTime)}
            </p>

            <h3 className="text-white text-lg font-medium">
              {selectedSchedule?.show?.name}
            </h3>
          </div>
        </div>

        {/* Description */}
        {selectedSchedule?.show?.description && (
          <p className="text-white/80 text-sm mb-4">
            {selectedSchedule?.show?.description}
          </p>
        )}

        {/* Details */}
        <div className="space-y-3 text-sm mb-6">
          <div className="flex justify-between">
            <span className="text-white/70">Host:</span>
            <span className="text-white font-medium">
              {selectedSchedule?.show?.hostName}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-white/70">Type:</span>

            <div className="flex gap-2 flex-wrap justify-end">
              {selectedSchedule?.show?.categories?.map((category) => (
                <span
                  key={category?.id}
                  className="text-cyan-200 bg-cyan-900/30 border border-cyan-500/30 px-2 py-0.5 rounded text-xs"
                >
                  {category?.name}
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <span className="text-white/70">Status:</span>

            <span className="bg-green-900/30 border border-green-500/30 text-green-200 px-2 py-0.5 rounded text-xs">
              {selectedSchedule?.status}
            </span>
          </div>
        </div>

        {/* ---------------- Audio Player ---------------- */}
        <div className="border-t border-white/10 pt-4">
          {checkingMp3 ? (
            <p className="text-white/70 text-sm">Checking audio...</p>
          ) : mp3Exists ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <p className="text-white/70 text-xs mb-2">
                ▶ Now Playing Stream
              </p>

              <ScheduleAudioPlayer
                src={`${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${selectedSchedule?.id}`}
                currentPlaying={currentPlaying}
                setPlaying={setPlaying}
              />

            </div>
          ) : (
            <p className="text-white/70 text-sm">
              No MP3 available for this show
            </p>
          )}
        </div>
        
      </div>
    </div>
  );
}
